import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Receipt, Tag } from '@phosphor-icons/react';
import { toast } from 'sonner';

import { Button } from '../../components/ui/Button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/Dialog';
import { Input } from '../../components/ui/Input';
import { EmptyState } from '../../components/common/EmptyState';
import { PageContentShell } from '../../components/common/PageContentShell';
import { SectionSkeleton } from '../../components/common/SectionSkeleton';
import {
  useCategoriesQuery,
  useDeleteExpenseMutation,
  useExpensesInfiniteQuery,
  useUpdateExpenseMutation,
} from '../../features/expenses/queries';
import { ExpensesBudgetMonitor } from '../../components/expenses/ExpensesBudgetMonitor';
import { ExpensesCycleHero } from '../../components/expenses/ExpensesCycleHero';
import { ExpensesCycleList } from '../../components/expenses/ExpensesCycleList';
import { AlertsPanel } from '../../components/alerts/AlertsPanel';
import { buildDashboardAlerts } from '../../utils/dashboardAlerts';
import { useCycleFinancialOverview } from '../../hooks/useCycleFinancialOverview';
import { useQueryShellState } from '../../hooks/useQueryShellState';
import { useSelfieCycle } from '../../store/SelfieCycleContext';
import pt from '../../locales/pt';
import { extractMutationError } from '../../utils/apiError';
import { parsePtNumber } from '../../utils/money';
import { useExpenseSheet } from '../../features/expenses/SheetProvider';
import { useAuth } from '../../store/AuthContext';
import { isEditableOccurrenceDate } from '../../utils/editableCycle';
import { paths } from '../../routes/paths';
import { GuidedFlowReturnBanner } from '../../components/dashboard/GuidedFlowReturnBanner';
import { cycleDayNumber, cycleTotalDays } from '../../utils/cycleDays';
import { formatSelfiePeriodLabel, todayISO } from '../../utils/selfiePeriod';

export function Expenses() {
  const { profile } = useAuth();
  const today = new Date();
  const selfieDay = profile?.selfie_day_of_month;
  const {
    cycles,
    cycleIndex,
    setCycleIndex,
    selectedCycle: activeCycle,
    canEditSelected: canEditPeriod,
    isLoading: cyclesLoading,
  } = useSelfieCycle();

  const filters = useMemo(() => {
    if (!activeCycle) return null;
    return {
      occurred_on_from: activeCycle.start,
      occurred_on_to: activeCycle.end,
    };
  }, [activeCycle]);

  const {
    overview,
    monthlySummary,
    isPending: overviewPending,
    isFetching: overviewFetching,
    overviewQuery,
    monthlySummaryQuery,
  } = useCycleFinancialOverview(activeCycle);

  /** `.pen` → bloco `error` de ds/card/cycle-spend-hero. */
  const cycleIsError = overviewQuery?.isError || monthlySummaryQuery?.isError;
  const retryCycle = () => {
    overviewQuery?.refetch();
    monthlySummaryQuery?.refetch();
  };

  const overviewShell = useQueryShellState({
    data: activeCycle ? overview : undefined,
    isPending: cyclesLoading || (Boolean(activeCycle) && overviewPending),
    isFetching: Boolean(activeCycle) && overviewFetching,
    isPlaceholderData: Boolean(activeCycle) && Boolean(overviewQuery?.isPlaceholderData),
  });

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isPending: expensesPending,
    isPlaceholderData: expensesIsPlaceholder,
  } = useExpensesInfiniteQuery(filters, { enabled: Boolean(filters) });

  const listShell = useQueryShellState({
    data: filters ? data : undefined,
    isPending: cyclesLoading || (Boolean(filters) && expensesPending),
    isFetching: Boolean(filters) && isFetching,
    isPlaceholderData: Boolean(filters) && Boolean(expensesIsPlaceholder),
  });

  const { data: categories = [] } = useCategoriesQuery({ is_archived: false });

  const { open } = useExpenseSheet();
  const updateExpense = useUpdateExpenseMutation(filters);
  const deleteExpense = useDeleteExpenseMutation(filters);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const recurringToastShown = useRef(false);

  useEffect(() => {
    if (recurringToastShown.current || isFetching || !data?.pages?.length) return;
    const todayIso = todayISO();
    const all = data.pages.flatMap((page) => page?.results || []);
    const hasRecurringToday = all.some(
      (e) => e.source === 'recurring' && e.occurred_on === todayIso,
    );
    if (hasRecurringToday) {
      recurringToastShown.current = true;
      toast.message(pt.expenses.recurringToast);
    }
  }, [data, isFetching]);

  const allExpenses = useMemo(() => {
    if (!data?.pages) return [];
    return data.pages.flatMap((page) => page?.results || []);
  }, [data]);

  const hasCycleExpenses =
    allExpenses.length > 0 || Number(overview?.total_spent_eur ?? 0) > 0;
  const contentReady =
    !overviewShell.showBlockingSkeleton && !listShell.showBlockingSkeleton;
  const showEmptyCycle =
    contentReady &&
    !hasCycleExpenses &&
    Boolean(activeCycle);
  const showLayersBelowHero = hasCycleExpenses;

  const canEditExpense = (expense) =>
    isEditableOccurrenceDate(expense.occurred_on, selfieDay, today, cycles);

  const desktopCycleSub = useMemo(() => {
    if (!activeCycle?.start || !activeCycle?.end) return pt.expenses.subtitle;
    const period = formatSelfiePeriodLabel(activeCycle.start, activeCycle.end);
    const day = cycleDayNumber(activeCycle);
    const total = cycleTotalDays(activeCycle);
    if (day == null || total == null) return pt.expenses.subtitle;
    return pt.expenses.desktopCycleSub
      .replace('{period}', period)
      .replace('{day}', String(day))
      .replace('{total}', String(total));
  }, [activeCycle]);

  const onConfirmDelete = async () => {
    if (!deleting) return;
    try {
      await deleteExpense.mutateAsync(deleting.id);
      toast.success(pt.expenses.actions.deleted);
      setDeleting(null);
    } catch (err) {
      toast.error(typeof err === 'string' ? err : pt.expenses.actions.deleteError);
    }
  };

  const hero = activeCycle ? (
    <ExpensesCycleHero
      activeCycle={activeCycle}
      cycles={cycles}
      cycleIndex={cycleIndex}
      onCycleIndexChange={setCycleIndex}
      overview={overview}
      monthlySummary={monthlySummary}
      isError={cycleIsError}
      onRetry={retryCycle}
    />
  ) : null;

  /** Só alertas de orçamento/categoria — sem contexto de FE nesta página. */
  const expensesAlerts = useMemo(() => buildDashboardAlerts(overview), [overview]);

  const contentFallback = (
    <div className="flex flex-col gap-space-5">
      <SectionSkeleton variant="hero" />
      <div className="grid grid-cols-1 gap-space-5 md:grid-cols-2">
        <SectionSkeleton variant="list" />
        <SectionSkeleton variant="card-grid" className="grid-cols-1 sm:grid-cols-1" />
      </div>
    </div>
  );

  return (
    <div className="flex w-full min-w-0 flex-col gap-space-5 overflow-x-hidden">
      <GuidedFlowReturnBanner />
      <header className="hidden flex-col gap-space-4 md:flex sm:flex-row sm:items-center sm:justify-between">
        <div className="flex min-w-0 flex-col gap-0.5">
          <h1 className="type-title-page">{pt.expenses.title}</h1>
          <p className="type-body-sm text-muted-foreground">{desktopCycleSub}</p>
        </div>
        <div className="flex shrink-0 items-center gap-space-3">
          <Button variant="secondary" size="sm" asChild>
            <Link to={paths.categories}>
              <Tag className="h-4 w-4" aria-hidden />
              {pt.expenses.hero.manageCategories}
            </Link>
          </Button>
          {canEditPeriod ? (
            <Button type="button" size="sm" onClick={() => open()}>
              <Plus className="h-4 w-4" aria-hidden />
              {pt.quickExpense.title}
            </Button>
          ) : null}
        </div>
      </header>

      <PageContentShell
        shells={[overviewShell, listShell]}
        fallback={contentFallback}
        className="flex flex-col gap-space-5"
      >
        {showLayersBelowHero ? (
          <div className="grid grid-cols-1 gap-space-5 md:grid-cols-2 md:items-start lg:grid-cols-[1.78fr_1fr]">
            <div className="flex min-w-0 flex-col gap-space-5">
              {hero}
              <AlertsPanel alerts={expensesAlerts} />
            </div>
            <div className="min-w-0 md:col-start-2 md:row-span-2">
              <ExpensesBudgetMonitor
                overview={overview}
                hidden={!hasCycleExpenses}
              />
            </div>
            <div className="flex min-w-0 flex-col gap-space-5 md:col-start-1">
              <ExpensesCycleList
                expenses={allExpenses}
                isFetching={isFetching}
                hasNextPage={hasNextPage}
                onLoadMore={() => fetchNextPage()}
                canEditExpense={canEditExpense}
                onEdit={setEditing}
                onDelete={setDeleting}
              />
            </div>
          </div>
        ) : (
          <>
            {hero}
            {showEmptyCycle ? (
              <EmptyState
                icon={Receipt}
                title={pt.expenses.empty.title}
                description={pt.expenses.empty.subtitle}
                action={
                  canEditPeriod ? (
                    <Button type="button" onClick={() => open()}>
                      <Plus className="mr-2 h-4 w-4" aria-hidden />
                      {pt.expenses.empty.cta}
                    </Button>
                  ) : null
                }
              />
            ) : null}
          </>
        )}
      </PageContentShell>

      <Dialog
        open={Boolean(editing)}
        onOpenChange={(openDialog) => {
          if (!openDialog) setEditing(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{pt.expenses.edit.title}</DialogTitle>
            <DialogDescription>{editing?.category_name}</DialogDescription>
          </DialogHeader>
          {editing ? (
            <EditExpenseForm
              key={editing.id}
              expense={editing}
              categories={categories}
              isPending={updateExpense.isPending}
              onCancel={() => setEditing(null)}
              onSubmit={async (payload) => {
                try {
                  await updateExpense.mutateAsync({ id: editing.id, payload });
                  toast.success(pt.expenses.actions.updated);
                  setEditing(null);
                } catch (err) {
                  toast.error(extractMutationError(err, 'Não foi possível guardar.'));
                }
              }}
            />
          ) : null}
        </DialogContent>
      </Dialog>

      <Dialog
        open={Boolean(deleting)}
        onOpenChange={(openDialog) => {
          if (!openDialog) setDeleting(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{pt.expenses.actions.deleteConfirmTitle}</DialogTitle>
            <DialogDescription>
              {pt.expenses.actions.deleteConfirmBody}
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setDeleting(null)}>
              {pt.common.cancel}
            </Button>
            <Button
              variant="destructive"
              onClick={onConfirmDelete}
              disabled={deleteExpense.isPending}
            >
              {deleteExpense.isPending ? pt.common.loading : pt.expenses.actions.deleteConfirm}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function resolveExpenseCategoryId(expense) {
  const raw = expense?.category;
  if (raw && typeof raw === 'object') return raw.id ?? '';
  return raw ?? '';
}

function EditExpenseForm({ expense, categories, onSubmit, onCancel, isPending }) {
  const [amount, setAmount] = useState(String(expense.amount).replace('.', ','));
  const [categoryId, setCategoryId] = useState(() => resolveExpenseCategoryId(expense));
  const [date, setDate] = useState(expense.occurred_on);
  const [description, setDescription] = useState(expense.description || '');
  const [formError, setFormError] = useState(null);

  const submit = (event) => {
    event.preventDefault();
    setFormError(null);
    const numericAmount = parsePtNumber(amount);
    if (!numericAmount || numericAmount <= 0) {
      setFormError(pt.quickExpense.amountLabel);
      return;
    }
    if (!categoryId) {
      setFormError(pt.quickExpense.pickCategory);
      return;
    }
    onSubmit({
      category: categoryId,
      amount: numericAmount.toFixed(2),
      occurred_on: date,
      description,
    });
  };

  return (
    <form className="flex flex-col gap-3" onSubmit={submit}>
      <div className="flex flex-col gap-1">
        <label className="text-sm font-medium text-muted-foreground">
          {pt.quickExpense.amountLabel}
        </label>
        <Input value={amount} onChange={(event) => setAmount(event.target.value)} />
      </div>
      <div className="flex flex-col gap-1">
        <label className="text-sm font-medium text-muted-foreground">
          {pt.quickExpense.categoryLabel}
        </label>
        <select
          className="h-11 rounded-xl border border-border bg-card px-3 text-sm"
          value={categoryId}
          onChange={(event) => setCategoryId(event.target.value)}
        >
          {categories.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
      </div>
      <div className="flex flex-col gap-1">
        <label className="text-sm font-medium text-muted-foreground">
          {pt.quickExpense.dateLabel}
        </label>
        <Input type="date" value={date} onChange={(event) => setDate(event.target.value)} />
      </div>
      <div className="flex flex-col gap-1">
        <label className="text-sm font-medium text-muted-foreground">
          {pt.quickExpense.descriptionLabel}
        </label>
        <Input
          value={description}
          onChange={(event) => setDescription(event.target.value)}
        />
      </div>
      {formError ? (
        <p className="text-sm text-error" role="alert">
          {formError}
        </p>
      ) : null}
      <div className="flex justify-end gap-2">
        <Button type="button" variant="ghost" onClick={onCancel}>
          {pt.common.cancel}
        </Button>
        <Button type="submit" disabled={isPending}>
          {isPending ? pt.common.loading : pt.expenses.edit.submit}
        </Button>
      </div>
    </form>
  );
}
